function safeArray(value) {
    return Array.isArray(value) ? value : [];
}

function getPageTitle(page) {
    return page?.title || page?.Title || page?.key || page?.Key || "Page";
}

function getPageKey(page) {
    return page?.key || page?.Key || "";
}

function getThemeLabel(project) {
    return (
        project?.themeKey ||
        project?.ThemeKey ||
        project?.theme_key ||
        "default-theme"
    );
}

function getBusinessName(project) {
    return (
        project?.businessName ||
        project?.BusinessName ||
        project?.projectName ||
        project?.ProjectName ||
        "Your Website"
    );
}

function getGoal(project, content) {
    return (
        project?.goalOfWebsite ||
        project?.primaryGoal ||
        project?.PrimaryGoal ||
        content?.meta?.primaryGoal ||
        "Business growth"
    );
}

function getIndustry(project, content) {
    return (
        project?.industry ||
        project?.Industry ||
        content?.meta?.industry ||
        "General business"
    );
}

function getSubIndustry(project, content) {
    return (
        project?.subIndustry ||
        project?.SubIndustry ||
        content?.meta?.subIndustry ||
        ""
    );
}

export default function GeneratedWebsiteExperience({
    project,
    preview,
    onPublish,
    onRegenerate,
    onOpenPreview
}) {
    const content = preview?.content || null;
    const pages = safeArray(preview?.pages);
    const businessName = getBusinessName(project);
    const liveUrl = preview?.liveUrl || project?.liveUrl || "";
    const themeLabel = getThemeLabel(project);

    const heroTitle =
        content?.hero?.title ||
        "Your AI-generated website is ready to review.";

    const heroSubtitle =
        content?.hero?.subtitle ||
        "Review the generated website, check the page structure, and choose the next action.";

    const highlights = [
        "AI-generated structure",
        "Multi-page website",
        "SEO-ready metadata",
        "Responsive layout",
        "Conversion-focused sections",
        "Ready for publish/regenerate flow"
    ];

    return (
        <div className="generated-experience">
            <div className="generated-hero-card">
                <div className="generated-hero-badge">Website Ready</div>

                <h2>{businessName}</h2>
                <p className="generated-hero-subtitle">{heroTitle}</p>
                <p className="generated-hero-description">{heroSubtitle}</p>

                <div className="generated-hero-actions">
                    <button
                        type="button"
                        className="gx-btn gx-btn-primary"
                        onClick={onOpenPreview}
                    >
                        View Live Preview
                    </button>

                    <button
                        type="button"
                        className="gx-btn gx-btn-success"
                        onClick={onPublish}
                    >
                        Publish Website
                    </button>

                    <button
                        type="button"
                        className="gx-btn gx-btn-secondary"
                        onClick={onRegenerate}
                    >
                        Regenerate Website
                    </button>
                </div>

                {liveUrl ? (
                    <div className="generated-url-row">
                        <span>Preview URL</span>
                        <a href={liveUrl} target="_blank" rel="noreferrer">
                            {liveUrl}
                        </a>
                    </div>
                ) : null}
            </div>

            <div className="generated-grid">
                <div className="generated-card">
                    <h3>Website Summary</h3>

                    <div className="generated-kv-grid">
                        <div className="generated-kv-item">
                            <span>Business</span>
                            <strong>{businessName}</strong>
                        </div>

                        <div className="generated-kv-item">
                            <span>Industry</span>
                            <strong>
                                {getIndustry(project, content)}
                                {getSubIndustry(project, content)
                                    ? ` / ${getSubIndustry(project, content)}`
                                    : ""}
                            </strong>
                        </div>

                        <div className="generated-kv-item">
                            <span>Goal</span>
                            <strong>{getGoal(project, content)}</strong>
                        </div>

                        <div className="generated-kv-item">
                            <span>Theme</span>
                            <strong>{themeLabel}</strong>
                        </div>

                        <div className="generated-kv-item">
                            <span>Status</span>
                            <strong>{preview?.status || project?.status || "Generated"}</strong>
                        </div>

                        <div className="generated-kv-item">
                            <span>Pages</span>
                            <strong>{pages.length}</strong>
                        </div>
                    </div>
                </div>

                <div className="generated-card">
                    <h3>Generated Pages</h3>

                    {pages.length > 0 ? (
                        <div className="generated-page-list">
                            {pages.map((page, index) => (
                                <div className="generated-page-item" key={`${getPageKey(page)}-${index}`}>
                                    <div className="generated-page-title">{getPageTitle(page)}</div>
                                    <div className="generated-page-key">{getPageKey(page) || "-"}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="generated-empty">
                            Page structure will appear here after generation.
                        </div>
                    )}
                </div>

                <div className="generated-card">
                    <h3>AI Highlights</h3>

                    <div className="generated-pill-list">
                        {highlights.map((item) => (
                            <span className="generated-pill" key={item}>
                                {item}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="generated-card">
                    <h3>Hero Copy</h3>

                    <div className="generated-copy-block">
                        <label>Headline</label>
                        <div>{content?.hero?.title || "-"}</div>
                    </div>

                    <div className="generated-copy-block">
                        <label>Subtitle</label>
                        <div>{content?.hero?.subtitle || "-"}</div>
                    </div>

                    <div className="generated-copy-block">
                        <label>Primary CTA</label>
                        <div>{content?.hero?.primaryCta || "-"}</div>
                    </div>

                    <div className="generated-copy-block">
                        <label>Secondary CTA</label>
                        <div>{content?.hero?.secondaryCta || "-"}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}